const OFFER_ROLES = ['qualifying_product'];

class FnkCartUpsellPopup extends HTMLElement {
  #loading = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#bindDialog();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.querySelector('dialog')?.removeEventListener('cancel', this.#handleCancel);
  }

  async openFor(sourceRole, productId) {
    if (this.dataset.enabled !== 'true' || !OFFER_ROLES.includes(sourceRole) || this.#loading) return;

    this.#loading = true;
    try {
      await this.#loadRecommendations(productId);
    } catch (error) {
      console.warn('FNK upsell recommendations could not be refreshed; using configured products.', error);
    } finally {
      this.#loading = false;
    }

    const qualifyingItemQuantity = Number(this.dataset.cartQualifyingItemQuantity || 0);
    const discountedChainQuantity = Number(this.dataset.cartDiscountedChainQuantity || 0);
    let targetRole = '';
    if (qualifyingItemQuantity > discountedChainQuantity) {
      targetRole = 'discounted_chain';
    } else if (discountedChainQuantity > qualifyingItemQuantity) {
      targetRole = 'qualifying_product';
    }

    if (!targetRole) {
      this.#continueToCart();
      return;
    }

    this.#open(targetRole);
  }

  close() {
    this.#close();
  }

  #handleClick = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[data-upsell-action]') : null;
    if (!control) return;

    const action = control.dataset.upsellAction;
    if (action === 'continue') {
      this.#continueToCart();
    } else if (action === 'previous' || action === 'next') {
      this.#scrollCarousel(action === 'next' ? 1 : -1);
    } else if (action === 'backdrop' && event.target === control) {
      this.#continueToCart();
    }
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#continueToCart();
  };

  async #loadRecommendations(productId) {
    if (!productId) return;

    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    url.searchParams.set('intent', 'related');

    const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const refreshedPopup = parsed.querySelector('fnk-cart-upsell-popup');
    const refreshedShell = refreshedPopup?.querySelector('[data-upsell-shell]');
    const currentShell = this.querySelector('[data-upsell-shell]');

    if (refreshedShell && currentShell) {
      currentShell.replaceWith(refreshedShell);
      this.#bindDialog();
    }

    if (refreshedPopup) {
      this.dataset.cartQualifyingItemQuantity = refreshedPopup.dataset.cartQualifyingItemQuantity || '0';
      this.dataset.cartDiscountedChainQuantity = refreshedPopup.dataset.cartDiscountedChainQuantity || '0';
    }
  }

  #bindDialog() {
    const dialog = this.querySelector('dialog');
    dialog?.removeEventListener('cancel', this.#handleCancel);
    dialog?.addEventListener('cancel', this.#handleCancel);
  }

  #open(targetRole) {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    let activePanel = null;
    for (const panel of this.querySelectorAll('[data-upsell-panel]')) {
      const isActive = panel.dataset.upsellPanel === targetRole;
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    }

    if (!activePanel || !activePanel.querySelector('[data-upsell-card]')) {
      this.#continueToCart();
      return;
    }

    document.documentElement.classList.add('fnk-upsell-open');
    dialog.showModal();
    requestAnimationFrame(() => this.querySelector('[data-upsell-action="continue"]')?.focus());
  }

  #close() {
    const dialog = this.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    document.documentElement.classList.remove('fnk-upsell-open');
  }

  #continueToCart() {
    this.#close();

    const drawer = document.querySelector('cart-drawer-component');
    if (this.dataset.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    window.location.assign(Theme.routes.cart_url);
  }

  #scrollCarousel(direction) {
    const activePanel = this.querySelector('[data-upsell-panel]:not([hidden])');
    const track = activePanel?.querySelector('[data-upsell-track]');
    if (!(track instanceof HTMLElement)) return;

    track.scrollBy({ left: direction * Math.max(track.clientWidth * 0.82, 260), behavior: 'smooth' });
  }
}

if (!customElements.get('fnk-cart-upsell-popup')) {
  customElements.define('fnk-cart-upsell-popup', FnkCartUpsellPopup);
}
